/**
 * Video Reel Component - Shopify Broadcast Theme
 * Gestisce scroll verticale, lazy loading, e controlli video
 */

class VideoReelComponent {
  constructor(container) {
    this.container = container;
    this.wrapper = container.querySelector('.video-reel-wrapper');
    this.items = container.querySelectorAll('.video-reel-item');
    this.videos = container.querySelectorAll('.reel-video');
    this.indicators = container.querySelectorAll('.indicator-dot');
    this.playPauseBtns = container.querySelectorAll('.play-pause-btn');
    
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.touchStartY = 0;
    this.touchEndY = 0;
    
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupIntersectionObserver();
    this.loadCurrentVideo();
    this.showCurrentItem();
    this.updateIndicators();
  }

  setupEventListeners() {
    // Touch Events per swipe
    this.wrapper.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.wrapper.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    
    // Mouse wheel per desktop
    this.wrapper.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
    
    // Keyboard navigation
    this.container.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // Play/Pause button events
    this.playPauseBtns.forEach((btn, index) => {
      btn.addEventListener('click', () => this.toggleVideoPlayback(index));
    });
    
    // Indicator navigation
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Video events
    this.videos.forEach((video, index) => {
      video.addEventListener('loadstart', () => this.showLoading(index));
      video.addEventListener('canplay', () => this.hideLoading(index));
      video.addEventListener('ended', () => this.handleVideoEnd(index));
      video.addEventListener('click', () => this.toggleVideoPlayback(index));
    });

    // Pause other videos when one starts playing
    this.videos.forEach((video, index) => {
      video.addEventListener('play', () => {
        this.videos.forEach((otherVideo, otherIndex) => {
          if (otherIndex !== index && !otherVideo.paused) {
            otherVideo.pause();
          }
        });
      });
    });

    // Visibility change - pause videos when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.videos.forEach(video => video.pause());
      }
    });
  }

  setupIntersectionObserver() {
    const options = {
      root: this.wrapper,
      rootMargin: '0px',
      threshold: 0.5
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target.querySelector('.reel-video');
        if (entry.isIntersecting) {
          this.loadVideo(video);
        } else {
          video.pause();
        }
      });
    }, options);

    this.items.forEach(item => {
      this.observer.observe(item);
    });
  }

  handleTouchStart(e) {
    this.touchStartY = e.touches[0].clientY;
  }

  handleTouchEnd(e) {
    this.touchEndY = e.changedTouches[0].clientY;
    this.handleSwipe();
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const swipeDistance = this.touchStartY - this.touchEndY;

    if (Math.abs(swipeDistance) < swipeThreshold) return;

    if (swipeDistance > 0) {
      // Swipe up - next video
      this.nextSlide();
    } else {
      // Swipe down - previous video
      this.prevSlide();
    }
  }

  handleWheel(e) {
    e.preventDefault();
    
    if (this.isTransitioning) return;
    
    const delta = e.deltaY;
    const threshold = 10;

    if (delta > threshold) {
      this.nextSlide();
    } else if (delta < -threshold) {
      this.prevSlide();
    }
  }

  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        this.prevSlide();
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.nextSlide();
        break;
      case ' ':
        e.preventDefault();
        this.toggleVideoPlayback(this.currentIndex);
        break;
    }
  }

  nextSlide() {
    if (this.currentIndex < this.items.length - 1) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  goToSlide(index) {
    if (index === this.currentIndex || this.isTransitioning) return;
    
    this.isTransitioning = true;
    
    // Pause current video
    const currentVideo = this.videos[this.currentIndex];
    if (currentVideo && !currentVideo.paused) {
      currentVideo.pause();
    }
    
    this.currentIndex = index;
    this.showCurrentItem();
    this.updateIndicators();
    this.loadCurrentVideo();
    
    // Reset transition flag after animation
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  }

  showCurrentItem() {
    this.items.forEach((item, index) => {
      item.classList.remove('active', 'prev');
      
      if (index === this.currentIndex) {
        item.classList.add('active');
      } else if (index < this.currentIndex) {
        item.classList.add('prev');
      }
    });
  }

  updateIndicators() {
    this.indicators.forEach((indicator, index) => {
      indicator.classList.toggle('active', index === this.currentIndex);
    });
  }

  loadCurrentVideo() {
    const currentVideo = this.videos[this.currentIndex];
    if (currentVideo) {
      this.loadVideo(currentVideo);
    }
  }

  loadVideo(video) {
    if (!video) return;
    
    // Rimuovi lazy loading per il primo video
    const isFirstVideo = Array.from(this.videos).indexOf(video) === 0;
    
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
      
      // Force load per primo video
      if (isFirstVideo) {
        video.load();
        video.preload = 'auto';
      }
    }
    
    // Timeout per evitare loading infinito
    const loadTimeout = setTimeout(() => {
      this.hideLoading(Array.from(this.videos).indexOf(video));
      console.warn('Video loading timeout - check video format/size');
    }, 10000);
    
    video.addEventListener('loadeddata', () => {
      clearTimeout(loadTimeout);
      this.hideLoading(Array.from(this.videos).indexOf(video));
    }, { once: true });
  }

  toggleVideoPlayback(index) {
    const video = this.videos[index];
    if (!video) return;

    const playBtn = video.closest('.video-reel-item').querySelector('.play-icon');
    const pauseBtn = video.closest('.video-reel-item').querySelector('.pause-icon');
    const overlay = video.closest('.video-reel-item').querySelector('.video-overlay');

    if (video.paused) {
      video.play().then(() => {
        playBtn.classList.add('hidden');
        pauseBtn.classList.remove('hidden');
        overlay.classList.remove('show');
      }).catch(error => {
        console.warn('Video play failed:', error);
        this.showPlayButton(index);
      });
    } else {
      video.pause();
      playBtn.classList.remove('hidden');
      pauseBtn.classList.add('hidden');
      overlay.classList.add('show');
    }
  }

  showPlayButton(index) {
    const item = this.items[index];
    const playBtn = item.querySelector('.play-icon');
    const pauseBtn = item.querySelector('.pause-icon');
    const overlay = item.querySelector('.video-overlay');

    playBtn.classList.remove('hidden');
    pauseBtn.classList.add('hidden');
    overlay.classList.add('show');
  }

  handleVideoEnd(index) {
    this.showPlayButton(index);
    
    // Auto advance to next video if available
    if (index < this.items.length - 1) {
      setTimeout(() => {
        this.nextSlide();
      }, 500);
    }
  }

  showLoading(index) {
    const item = this.items[index];
    const existingLoader = item.querySelector('.video-loading');
    
    if (!existingLoader) {
      const loader = document.createElement('div');
      loader.className = 'video-loading';
      loader.textContent = 'Caricamento...';
      item.querySelector('.video-container').appendChild(loader);
    }
  }

  hideLoading(index) {
    const item = this.items[index];
    const loader = item.querySelector('.video-loading');
    if (loader) {
      loader.remove();
    }
  }

  // Public method per inizializzare il componente
  static init() {
    const reelSections = document.querySelectorAll('.video-reel-section');
    reelSections.forEach(section => {
      new VideoReelComponent(section);
    });
  }

  // Cleanup method per rimozione event listeners
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Remove event listeners
    this.wrapper.removeEventListener('touchstart', this.handleTouchStart);
    this.wrapper.removeEventListener('touchend', this.handleTouchEnd);
    this.wrapper.removeEventListener('wheel', this.handleWheel);
    this.container.removeEventListener('keydown', this.handleKeydown);
  }
}

// Initialize quando il DOM è pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', VideoReelComponent.init);
} else {
  VideoReelComponent.init();
}

// Reinizializza dopo AJAX/Shopify section reload
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('.video-reel-section')) {
    setTimeout(() => VideoReelComponent.init(), 100);
  }
});

// Export per uso modulare se necessario
window.VideoReelComponent = VideoReelComponent;